import React, { PropsWithChildren } from 'react';
import { Flex, FlexProps } from '../../legacy/Flex';
import { TypographyProps } from '../Typography';
import { ActionItem } from './ActionItem';
import { Context, defaultContextValue } from './Context';
import css from './index.module.scss';
import { LabelItem } from './LabelItem';
import { LinkItem } from './LinkItem';
import { Separator } from './Separator';

export interface InfoListProps {
  /**
   * The styles of typography to be used for all list items.
   *
   * Defaults to: {
   *   color: 'neutrals-n7',
   *   variant: 'body2'
   * }
   */
  typography?: Partial<TypographyProps>;

  /**
   * Determines what the wrap behavior of the items is.
   */
  wrap?: FlexProps['flexWrap'];

  /**
   * Links, by default, are blue. But if inherit is set to true
   * they will inherit the color defined in typography styles above.
   *
   * Defaults to blue.
   */
  linkColor?: 'blue' | 'inherit';

  testId?: string;
}

/**
 * @deprecated
 * Deprecated in favor of using composition. See the patterns section in storybook for more info on how to build
 * an Info List pattern.
 */
const InfoList: React.FunctionComponent<PropsWithChildren<InfoListProps>> = ({
  testId = defaultContextValue.testId,
  children,
  typography,
  linkColor = defaultContextValue.linkColor,
  wrap = 'wrap',
}) => (
  <Context.Provider
    value={{
      linkColor,
      testId,
      typography: {
        /**
         * Spread this so that partial styles can be provided in
         * the typography prop and the remaining defined in the defaults
         * stay.
         */
        ...defaultContextValue.typography,
        ...typography,
      },
    }}
  >
    <Flex
      data-component="InfoList"
      testId={testId}
      alignItems="flex-start"
      justifyContent="flex-start"
      flexWrap={wrap}
      className={css.listItems}
    >
      {children}
    </Flex>
  </Context.Provider>
);

const InfoListWithStatics = InfoList as typeof InfoList & {
  ActionItem: typeof ActionItem;
  LinkItem: typeof LinkItem;
  LabelItem: typeof LabelItem;
  Separator: typeof Separator;
};
InfoListWithStatics.ActionItem = ActionItem;
InfoListWithStatics.LinkItem = LinkItem;
InfoListWithStatics.LabelItem = LabelItem;
InfoListWithStatics.Separator = Separator;

export { InfoListWithStatics as InfoList, InfoList as InfoListWithoutStatics };
